<template>
  <div class="tag-input" :class="{ 'tag-input--error': externalError }" @keydown.esc="closeDropdown">
    <label v-if="label" class="tag-input__label">{{ label }}</label>

    <!-- Selected chips -->
    <div class="tag-input__chips" v-if="selected.length > 0">
      <span
        v-for="(item, idx) in selected"
        :key="idx"
        class="tag-input__chip"
      >
        {{ item.name }}
        <button
          type="button"
          class="tag-input__chip-remove"
          :aria-label="'Elimină ' + item.name"
          @click="removeItem(idx)"
        >×</button>
      </span>
    </div>

    <!-- Search input row -->
    <div class="tag-input__search-row">
      <input
        ref="inputRef"
        type="text"
        class="tag-input__input"
        :placeholder="selected.length === 0 ? placeholder : 'Adaugă...'"
        :value="searchTerm"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.enter.prevent="onEnterKey"
        @keydown.backspace="onBackspace"
      />
    </div>

    <!-- Dropdown suggestions -->
    <div v-if="isOpen" class="tag-input__dropdown">
      <div v-if="isLoading" class="tag-input__state">Se încarcă...</div>
      <template v-else>
        <button
          v-for="option in filteredOptions"
          :key="option.id"
          type="button"
          class="tag-input__option"
          @mousedown.prevent="selectOption(option)"
        >
          {{ option.name }}
        </button>
        <button
          v-if="canAddNew"
          type="button"
          class="tag-input__option tag-input__option--new"
          @mousedown.prevent="addNew"
        >
          + Adaugă „{{ searchTerm.trim() }}"
        </button>
        <div
          v-if="filteredOptions.length === 0 && !canAddNew"
          class="tag-input__state"
        >
          Nu există rezultate.
        </div>
      </template>
    </div>

    <div v-if="externalError || errorMessage" class="tag-input__error">
      {{ externalError || errorMessage }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'TagInput',
  props: {
    modelValue: {
      // Array of { id: number|null, name: string }
      type: Array,
      default: () => [],
    },
    endpoint: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Caută sau adaugă...',
    },
    errors: {
      type: Array,
      default: () => [],
    },
    minimumSearchLength: {
      type: Number,
      default: 0,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      searchTerm: '',
      options: [],
      isOpen: false,
      isLoading: false,
      errorMessage: '',
      debounceTimer: null,
    };
  },
  computed: {
    externalError() {
      return Array.isArray(this.errors) && this.errors.length > 0 ? this.errors[0] : null;
    },
    selected() {
      return Array.isArray(this.modelValue) ? this.modelValue : [];
    },
    trimmedSearch() {
      return this.searchTerm.trim();
    },
    selectedNames() {
      return this.selected.map((s) => s.name.toLowerCase());
    },
    filteredOptions() {
      return this.options.filter(
        (opt) => !this.selectedNames.includes(opt.name.toLowerCase())
      );
    },
    canAddNew() {
      if (this.trimmedSearch.length === 0) return false;
      if (this.selectedNames.includes(this.trimmedSearch.toLowerCase())) return false;
      const alreadyInOptions = this.options.some(
        (opt) => opt.name.toLowerCase() === this.trimmedSearch.toLowerCase()
      );
      return !alreadyInOptions;
    },
  },
  mounted() {
    this.fetchOptions('');
  },
  beforeUnmount() {
    clearTimeout(this.debounceTimer);
  },
  methods: {
    async fetchOptions(query) {
      if (query.length > 0 && query.length < this.minimumSearchLength) {
        this.options = [];
        return;
      }

      this.isLoading = true;
      this.errorMessage = '';

      try {
        const response = await window.axios.get(this.endpoint, {
          params: { q: query },
        });
        this.options = Array.isArray(response.data?.data) ? response.data.data : [];
      } catch {
        this.errorMessage = 'Nu am putut încărca datele.';
        this.options = [];
      } finally {
        this.isLoading = false;
      }
    },

    onInput(event) {
      this.searchTerm = event.target.value;
      this.isOpen = true;

      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.fetchOptions(this.trimmedSearch);
      }, 250);
    },

    onFocus() {
      this.isOpen = true;
      if (this.options.length === 0) {
        this.fetchOptions(this.trimmedSearch);
      }
    },

    onBlur() {
      setTimeout(() => {
        this.isOpen = false;
      }, 120);
    },

    closeDropdown() {
      this.isOpen = false;
    },

    selectOption(option) {
      if (!this.selectedNames.includes(option.name.toLowerCase())) {
        this.$emit('update:modelValue', [
          ...this.selected,
          { id: option.id, name: option.name },
        ]);
      }
      this.searchTerm = '';
      this.isOpen = false;
      this.$refs.inputRef.blur();
    },

    addNew() {
      const name = this.trimmedSearch;
      if (!name || this.selectedNames.includes(name.toLowerCase())) return;

      this.$emit('update:modelValue', [
        ...this.selected,
        { id: null, name },
      ]);
      this.searchTerm = '';
      this.isOpen = false;
      this.$refs.inputRef.blur();
    },

    removeItem(idx) {
      const updated = [...this.selected];
      updated.splice(idx, 1);
      this.$emit('update:modelValue', updated);
    },

    onEnterKey() {
      if (!this.isOpen) {
        this.isOpen = true;
        return;
      }
      if (this.filteredOptions.length > 0) {
        this.selectOption(this.filteredOptions[0]);
        return;
      }
      if (this.canAddNew) {
        this.addNew();
      }
    },

    onBackspace() {
      if (this.searchTerm === '' && this.selected.length > 0) {
        this.removeItem(this.selected.length - 1);
      }
    },
  },
};
</script>
